import { z } from 'zod';

/** Forma única de error en toda la API. La interfaz decide qué pintar a partir de `code`,
 *  nunca del texto de `message`, que puede cambiar sin previo aviso. */
export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

export const paginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type Pagination = z.infer<typeof paginationSchema>;

export const serviceHealthSchema = z.enum(['ok', 'degraded', 'down', 'unconfigured']);
export type ServiceHealth = z.infer<typeof serviceHealthSchema>;

export const healthSchema = z.object({
  api: serviceHealthSchema,
  db: serviceHealthSchema,
  amule: z.object({
    status: serviceHealthSchema,
    ed2k: z.string().nullable(),
    kad: z.string().nullable(),
    highId: z.boolean().nullable(),
    detail: z.string().nullable(),
  }),
  plex: z.object({
    status: serviceHealthSchema,
    servers: z.number(),
    countedLibraries: z.number(),
    lastSync: z.string().nullable(),
    detail: z.string().nullable(),
  }),
  indexer: z.object({ status: serviceHealthSchema, detail: z.string().nullable() }),
  /** ¿Se puede colocar una película? (I-4 / P-7). No es un servicio externo, pero falla
   *  igual de fácil —una carpeta sin montar, una biblioteca sin ruta local— y hasta ahora
   *  solo se sabía al intentar colocar la primera, que es el peor momento. */
  colocacion: z.object({
    status: serviceHealthSchema,
    detail: z.string().nullable(),
  }),
});
export type Health = z.infer<typeof healthSchema>;

/** Espacio en disco. Se mide por **sistema de ficheros**, no por carpeta: las tres carpetas
 *  de D-15 —descargas, conversiones y cada biblioteca— suelen vivir en el mismo disco, y
 *  pintar tres veces los mismos 812 GB haría creer que hay 2,4 TB. */
export const rolCarpetaSchema = z.enum(['descargas', 'conversiones', 'biblioteca']);
export type RolCarpeta = z.infer<typeof rolCarpetaSchema>;

export const carpetaAlmacenamientoSchema = z.object({
  rol: rolCarpetaSchema,
  /** Cómo llamarla en la interfaz: «Descargas», «Conversiones» o el nombre de la biblioteca. */
  nombre: z.string(),
  ruta: z.string(),
});
export type CarpetaAlmacenamiento = z.infer<typeof carpetaAlmacenamientoSchema>;

export const sistemaDeFicherosSchema = z.object({
  /** El dispositivo que devuelve `stat`, en texto. Solo sirve para agrupar y como clave
   *  de React: no es un nombre estable entre reinicios. */
  id: z.string(),
  bytesTotales: z.number(),
  /** Lo que podemos usar de verdad (`bavail`), no `bfree`: los bloques reservados para
   *  root existen pero no son nuestros, y prometer un espacio en el que ffmpeg va a fallar
   *  es peor que no decir nada. */
  bytesLibres: z.number(),
  /** Total menos libres, para que los tres números cuadren entre sí. La reserva de root
   *  cae de este lado, así que `df` puede decir un punto porcentual menos. */
  bytesUsados: z.number(),
  porcentajeUsado: z.number(),
  carpetas: z.array(carpetaAlmacenamientoSchema),
});
export type SistemaDeFicheros = z.infer<typeof sistemaDeFicherosSchema>;

export const almacenamientoSchema = z.object({
  sistemas: z.array(sistemaDeFicherosSchema),
  /** Las que no se pudieron consultar: un volumen sin montar o una biblioteca cuya ruta
   *  local no existe en esta máquina. Callarlas las convertiría en «no hay disco», que es
   *  justo el diagnóstico equivocado. */
  inaccesibles: z.array(carpetaAlmacenamientoSchema.extend({ error: z.string() })),
  /** El umbral de D-14, para poder avisar en el sistema donde caen las descargas. */
  minLibreGb: z.number(),
});
export type Almacenamiento = z.infer<typeof almacenamientoSchema>;
